/**
 * useFunChallengesSocket
 *
 * Low-level envelope hook for the Fun Challenges feature.
 *
 * - Registers schemapack handlers for every `fun_*` message type once per
 *   socket. De-duped via a unique `handlerKey` so calling this hook from
 *   multiple components doesn't double-fire.
 * - Decodes each inbound `payload` (JSON string) and dispatches into the
 *   FunChallenges Zustand stores.
 * - Exposes a `send(type, gameType, id, payloadObj, requestId?)` helper that
 *   wraps `gameClient.send_msg` with the `funChallengeEnvelopeSchema` shape.
 *
 * The hook intentionally does NOT touch existing chess message
 * registrations on the same `gameClient` — only adds new types using a
 * separate schema (`funChallengeEnvelopeSchema`).
 *
 * Mount once near the top of the app tree (e.g. inside the global
 * `FunChallengesGlobalListener` from Phase 4) so `fun_room_invite` is
 * received regardless of the current screen.
 */

import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {create} from 'zustand';

import {SocketContext} from '@/Providers/SocketProvider';
import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import useFunChallengesSessionStore from '@/Store/funChallengesSessionStore';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';
import useFunChallengesTournamentStore from '@/Store/funChallengesTournamentStore';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {
  routeRoyaleRoomCreated,
  routeRoyaleRoomUpdate,
  routeRoyaleSessionStart,
  routeRoyaleMoveBroadcast,
  routeRoyalePlayerEliminated,
  routeRoyaleSessionEnd,
  routeRoyaleSessionRejoin,
  routeRoyaleSpectateStart,
  routeRoyaleSpectateMove,
  routeRoyaleRematch,
} from './royaleSocketRouting';
import {noteServerNow} from '@/Screens/FunChallenges/util/serverClock';
import {
  BattleType,
  type ChallengeSettings,
  type FunChallengeError,
  type FunChallengeEnvelope,
  type FunMoveBroadcastPayload,
  type FunRoomUpdatePayload,
  type FunSessionRejoinPayload,
  type FunSessionStartPayload,
  type IncomingChallengePayload,
} from '@/Screens/FunChallenges/types';
import {hardenDecode, hardenAllDecoders} from './hardenDecode';

const {schema} = require('../../../CCClientSdk');

/**
 * Shared registration signal. Tracks the socket identity that fun_* schemas
 * are currently registered against. Stored in a Zustand singleton so EVERY
 * `useFunChallengesSocket` consumer (global listener, lobby, battle screen)
 * subscribes to the same value. Without this, each consumer kept its own
 * `useState` copy and the lobby could remount with a stale `ready: false`
 * after a battle even though registration was actually live.
 */
interface FunRegistryState {
  registeredSocket: any | null;
  setRegisteredSocket: (s: any) => void;
}

const useFunChallengesRegistryStore = create<FunRegistryState>(set => ({
  registeredSocket: null,
  setRegisteredSocket: s => set({registeredSocket: s}),
}));

const HANDLER_PREFIX = 'FunChallenges:';

/**
 * Stable requestId we tag onto the speculative `fun_session_rejoin` we send
 * on every new socket. If the server replies with a NO_ACTIVE_SESSION /
 * SESSION_* error against this requestId, we treat it as "nothing to
 * rejoin" rather than a user-visible error.
 */
const REJOIN_PROBE_REQUEST_ID = 'fun-rejoin-probe';

// Tracks which socket identities have ALREADY had the auto-rejoin probe fired.
// useFunChallengesSocket is mounted by MANY components app-wide (the global
// listener, the in-progress pill, every battle/lobby screen). Without this
// guard each consumer's probe effect fires sendProbe() on mount whenever the
// socket is already authenticated — so a single shared socket got probed ~N
// times per render, and any re-render storm turned that into a flood of
// fun_session_rejoin that saturated the socket send queue and starved real
// messages like fun_room_create (the coach's royale create silently never
// reached the server). A WeakSet keyed on the socket object fires the probe
// exactly ONCE per socket identity across all consumers; it auto-forgets a
// socket once it's GC'd (a genuine reconnect makes a new socket → re-probes).
const PROBED_SOCKETS = new WeakSet<object>();

const FUN_INBOUND_TYPES = [
  'fun_room_created',
  'fun_room_invite',
  'fun_room_update',
  'fun_session_start',
  'fun_move_broadcast',
  'fun_session_end',
  'fun_session_rejoin',
  // Puzzle Royale (N-player): a player hit the punish mistake-out limit; the
  // race continues. Additive — 1v1 modes never receive it.
  'fun_player_eliminated',
  // Puzzle Royale: host started a rematch — the new lobby's code, pushed to
  // every ended-session player so they auto-join.
  'fun_rematch',
  // Puzzle Royale (web spectate): server bootstrap of a live race for a
  // spectator (late join / explicit spectate). Additive.
  'fun_spectate_start',
  // Puzzle Royale: a player's per-ply board stream, fanned out to spectators
  // so a followed board animates ply-by-ply. Additive.
  'fun_spectate_move',
  'fun_error',
  // Puzzle Royale TOURNAMENT: server → host echo of the durable id on create,
  // and lifecycle-state broadcasts (registered / live / cancelled) to anyone
  // with the lobby open. Additive.
  'fun_tournament_created',
  'fun_tournament_state',
] as const;

/**
 * Outbound (client → server) types. The SDK's `send_msg` checks whether the
 * event has a registered schema in `schemaTypeDict`; if not, the payload is
 * emitted as a plain JS object and the server's schemapack decoder rejects
 * it silently. We MUST register these schemas before any send.
 */
const FUN_OUTBOUND_TYPES = [
  'fun_room_create',
  'fun_room_join',
  'fun_room_leave',
  'fun_room_start',
  'fun_session_rejoin',
  'fun_move',
  'fun_forfeit',
  // Puzzle Royale: host → server rematch request (id = ended sessionId).
  'fun_rematch',
  // Puzzle Royale: client → server spectate request (id = share code/roomId).
  'fun_room_spectate',
  // Puzzle Royale: player → server per-ply board stream (id = sessionId).
  'fun_spectate_move',
  // Puzzle Royale: host → server play/spectate toggle (id = roomId).
  'fun_set_host_role',
  // Puzzle Royale TOURNAMENT (scheduled/hostable) control messages. Must be
  // registered here or send_msg emits them as plain objects and the server's
  // schemapack decoder rejects them silently (same gate as the others above).
  'fun_tournament_create',
  'fun_tournament_start_now',
  'fun_tournament_cancel',
  'fun_tournament_register',
  'fun_tournament_withdraw',
] as const;

function parsePayload<T = any>(raw: any): T | null {
  if (!raw) return null;
  if (typeof raw !== 'string') return raw as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function asBattleType(s: string | undefined): BattleType {
  return (s as BattleType) || BattleType.SQUARE_TRAINER;
}

/** True when an envelope belongs to Puzzle Royale (route to the royale store). */
function isRoyale(gameType: string | undefined): boolean {
  return gameType === BattleType.PUZZLE_ROYALE;
}

interface SendArgs {
  type: string;
  gameType: BattleType | string;
  id?: string;
  payload?: Record<string, any>;
  requestId?: string;
}

export interface FunChallengesSocketApi {
  /** Underlying socket — null until SocketProvider has connected. */
  socket: any | null;
  /** True iff the socket exists AND fun_* registrations are in place. */
  ready: boolean;
  /**
   * True once the socket has completed authentication (`auth_complete`). Use
   * this — NOT `ready` — to gate any auto-send on a cold load, since `ready`
   * only means the socket object exists, and sending before auth gets the
   * server's "auth failed" rejection.
   */
  authed: boolean;
  /** Send a fun_* envelope. No-op (returns false) if the socket isn't ready. */
  send: (args: SendArgs) => boolean;
}

export function useFunChallengesSocket(): FunChallengesSocketApi {
  const ctx = useContext(SocketContext);
  const socket = ctx?.socket ?? null;

  // Reactive readiness flag.
  // We keep the shared registry store as a *book-keeping* signal so consumers
  // see the moment a NEW socket gets schemas wired up — but we DON'T gate
  // `ready` on it, because the keyed `registerMessageHandler` path is itself
  // idempotent (the SDK replaces handlers by key). Earlier we strictly
  // required `registeredSocket === socket`, which left the Send button stuck
  // on "Connecting…" whenever the consumer rendered before the registry
  // store had caught up (race during fast-path mount, HMR-reset stores, or
  // a new socket after reconnect). The fix is to treat the presence of the
  // socket as ready, while still publishing to the registry for the
  // auto-rejoin / "is this a fresh socket?" code below.
  const registeredSocket = useFunChallengesRegistryStore(
    s => s.registeredSocket,
  );
  const setRegisteredSocket = useFunChallengesRegistryStore(
    s => s.setRegisteredSocket,
  );
  const ready = !!socket;

  // Reactive auth flag — true once `auth_complete` has fired (or the socket was
  // already authenticated when we attached). Consumers gate auto-sends on this
  // so a cold deep-link load doesn't fire before the server validates the
  // token (which would surface as "auth failed").
  const [authed, setAuthed] = useState<boolean>(() => !!socket?.authenticated);
  useEffect(() => {
    if (!socket) {
      setAuthed(false);
      return;
    }
    if (socket.authenticated) {
      setAuthed(true);
    }
    const onAuth = (data: any) => {
      if (data?.success || data?.user || socket.authenticated) {
        setAuthed(true);
      }
    };
    const onDrop = () => setAuthed(false);
    if (typeof socket.on === 'function') {
      socket.on('auth_complete', onAuth);
      socket.on('disconnect', onDrop);
      socket.on('close', onDrop);
    }
    return () => {
      try {
        socket.off?.('auth_complete', onAuth);
        socket.off?.('disconnect', onDrop);
        socket.off?.('close', onDrop);
      } catch {
        // ignore cleanup failures
      }
    };
  }, [socket]);

  const setRoom = useFunChallengesSessionStore(s => s.setRoom);
  const setRoomPlayers = useFunChallengesSessionStore(s => s.setRoomPlayers);
  const setRoomState = useFunChallengesSessionStore(s => s.setRoomState);
  const setSession = useFunChallengesSessionStore(s => s.setSession);
  const clearSession = useFunChallengesSessionStore(s => s.clearAll);

  const battleSetIncoming = useFunChallengesBattleStore(s => s.setIncomingChallenge);
  const battleStartInBattle = useFunChallengesBattleStore(s => s.startInBattle);
  const battleEnd = useFunChallengesBattleStore(s => s.endBattle);
  const battleSetError = useFunChallengesBattleStore(s => s.setError);
  const battleHydrate = useFunChallengesBattleStore(s => s.hydrateSession);
  const battleRecordOpponentMove = useFunChallengesBattleStore(
    s => s.recordOpponentMove,
  );

  // Resolved at call time so we always pick up the latest userId.
  // `loggedInUserstore.userId` is currently never populated (the chess
  // setUserId path lives in PlayChess/index.js and writes its own local
  // state). `userData.user_id` is the authoritative value set during
  // login (useLoginState → setUserData). Fall back to the store's userId
  // for safety if that path ever starts writing it.
  const myUserIdFromStore = useLoggedInUserStore(s => s.userId);
  const myUserIdFromUserData = useLoggedInUserStore(
    s => (s.userData && (s.userData.user_id ?? s.userData.pk)) || '',
  );
  const myUserId = String(
    myUserIdFromUserData || myUserIdFromStore || '',
  );
  // Own display name + avatar — used to populate the rematch auto-join so the
  // new lobby's roster shows real names, not blanks (#1).
  const myUsername = useLoggedInUserStore(
    s =>
      s.userData?.username ||
      s.userData?.name ||
      s.userData?.first_name ||
      s.email ||
      '',
  );
  const myAvatarUrl = useLoggedInUserStore(s =>
    typeof s.icon === 'string' ? s.icon : '',
  );

  // ── Inbound handlers ───────────────────────────────────────────────────
  const handlers = useMemo(() => {
    return {
      fun_room_created(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<{roomId: string; creatorId: string}>(
          data.payload,
        );
        if (!payload) return;
        if (isRoyale(data.gameType)) {
          // Royale: the façade (useRoyaleSocket) already seeded the lobby with
          // the host's chosen settings at send time. The server's ACK only
          // carries {roomId, creatorId}, so here we just confirm/refresh the
          // roomId + shareCode on the existing lobby (or seed a minimal one if
          // the optimistic lobby is somehow absent).
          routeRoyaleRoomCreated(payload, data.gameType, myUserId);
          return;
        }
        // We're the creator — the lobby UI is already in `challenging` state
        // from `sendChallenge`. Record the roomId so we can cancel.
        const battle = useFunChallengesBattleStore.getState();
        if (battle.pendingChallenge && !battle.pendingChallenge.roomId) {
          useFunChallengesBattleStore.setState({
            pendingChallenge: {
              ...battle.pendingChallenge,
              roomId: payload.roomId,
            },
          });
        }
        // Mirror this room for the creator so the global listener's
        // auto-start effect (which gates on roomPlayersCount >= 2) operates
        // on data tied to THIS challenge, not whatever stale invite room
        // might still be sitting in the session store from a prior battle.
        setRoom({
          roomId: payload.roomId,
          creatorId: payload.creatorId,
          gameType: data.gameType,
          settings: battle.pendingChallenge?.settings ?? ({} as ChallengeSettings),
          state: 'waiting',
          players: [{userId: payload.creatorId, username: '', avatarUrl: ''}],
        });
      },

      fun_room_invite(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<{
          roomId: string;
          creatorId: string;
          creatorName: string;
          creatorAvatarUrl?: string;
          settings: ChallengeSettings;
        }>(data.payload);
        if (!payload) return;

        // All invites flow into the store normally; the listener decides
        // what to render. When the user is still on the result modal
        // (`status === 'ended'`), `setIncomingChallenge` preserves
        // `session`/`endResult` so BattleScreen keeps the result visible
        // behind the listener's accept/deny modal (see store action).

        const incoming: IncomingChallengePayload = {
          roomId: payload.roomId,
          battleType: asBattleType(data.gameType),
          challengerId: payload.creatorId,
          challengerName: payload.creatorName,
          challengerAvatarUrl: payload.creatorAvatarUrl,
          settings: payload.settings,
        };
        battleSetIncoming(incoming);
        setRoom({
          roomId: payload.roomId,
          creatorId: payload.creatorId,
          gameType: data.gameType,
          settings: payload.settings,
          state: 'waiting',
          players: [],
        });
      },

      fun_room_update(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunRoomUpdatePayload>(data.payload);
        if (!payload) return;
        if (isRoyale(data.gameType)) {
          routeRoyaleRoomUpdate(
            {...(payload as any), roomId: data.id},
            data.gameType,
            myUserId,
          );
          return;
        }
        if (payload.players) setRoomPlayers(payload.players);
        if (payload.state) setRoomState(payload.state);
      },

      fun_session_start(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunSessionStartPayload>(data.payload);
        if (!payload) return;
        // Calibrate the device↔server clock skew BEFORE anything reads a
        // timestamp off this payload. See util/serverClock.ts — a device with a
        // mis-set wall clock otherwise mis-renders the whole race (5 min fast ⇒
        // 0:00 and no time to play). No-ops against an old server.
        noteServerNow((payload as any).serverNow);

        if (isRoyale(data.gameType)) {
          routeRoyaleSessionStart(payload as any, myUserId);
          return;
        }

        // Older server builds may not ship startsAt; treat absence as "no
        // countdown" so the battle is interactive immediately. This keeps
        // the client safe even if the cc-game-service rollout lags behind.
        const startsAt = parseInt(payload.startsAt || '', 10) || 0;
        setSession({
          sessionId: payload.sessionId,
          roomId: payload.roomId,
          gameType: data.gameType,
          playerIds: payload.playerIds,
          endsAt: parseInt(payload.endsAt, 10) || 0,
          settings: payload.settings,
          coords: payload.coords,
        });

        const opponentId =
          payload.playerIds.find(id => String(id) !== String(myUserId)) || '';

        // Best-effort opponentName resolution. The `fun_session_start`
        // payload only carries player IDs — names live in:
        //   - pendingChallenge   (the challenger picked them in the lobby)
        //   - incomingChallenge  (the accepter received them in the invite)
        //   - session-store room.players (mirrored from fun_room_update)
        // Try each in order so both sides of the battle render a real name
        // instead of "Opponent".
        const battleSnap = useFunChallengesBattleStore.getState();
        const sessionSnap = useFunChallengesSessionStore.getState();
        const pendingMatchesOpponent =
          battleSnap.pendingChallenge &&
          String(battleSnap.pendingChallenge.opponentId) === String(opponentId)
            ? battleSnap.pendingChallenge.opponentName
            : undefined;
        const incomingMatchesOpponent =
          battleSnap.incomingChallenge &&
          String(battleSnap.incomingChallenge.challengerId) ===
            String(opponentId)
            ? battleSnap.incomingChallenge.challengerName
            : undefined;
        const roomPlayerMatch = sessionSnap.room?.players?.find(
          p => String(p.userId) === String(opponentId),
        )?.username;
        const opponentName =
          (pendingMatchesOpponent && pendingMatchesOpponent.trim()) ||
          (incomingMatchesOpponent && incomingMatchesOpponent.trim()) ||
          (roomPlayerMatch && roomPlayerMatch.trim()) ||
          '';

        battleStartInBattle({
          battleId: payload.sessionId,
          battleType: asBattleType(data.gameType),
          myUserId: String(myUserId || ''),
          opponentId: String(opponentId),
          opponentName,
          startsAt,
          endsAt: parseInt(payload.endsAt, 10) || 0,
          settings: payload.settings,
          coords: payload.coords,
        });
      },

      fun_move_broadcast(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunMoveBroadcastPayload>(data.payload);
        if (!payload) return;
        if (isRoyale(data.gameType)) {
          routeRoyaleMoveBroadcast(payload as any);
          return;
        }
        battleRecordOpponentMove(
          String(payload.playerId),
          payload.correct,
          payload.wrong,
          payload.nextIndex,
          payload.square,
        );
      },

      fun_session_end(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<{
          sessionId: string;
          gameType: string;
          winnerId: string;
          rankedPlayers: string[];
          scores: Record<string, {correct: number; wrong: number}>;
          responses: Record<string, string[]>;
          reason: 'time_up' | 'forfeit' | 'mistake_out';
        }>(data.payload);
        if (!payload) return;
        if (isRoyale(data.gameType || payload.gameType)) {
          routeRoyaleSessionEnd(payload as any);
          return;
        }
        battleEnd({
          battleId: payload.sessionId,
          battleType: asBattleType(data.gameType || payload.gameType),
          winnerId: payload.winnerId || '',
          rankedPlayers: payload.rankedPlayers || [],
          scores: payload.scores || {},
          responses: payload.responses || {},
          reason: payload.reason || 'time_up',
        });
        clearSession();
      },

      // Puzzle Royale web spectate: the server bootstrapped us as a spectator
      // of a live race (explicit Spectate, or a late join after start). Royale
      // only; hydrate the store read-only and follow ongoing broadcasts.
      fun_spectate_start(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<any>(data.payload);
        if (!payload) return;
        noteServerNow(payload.serverNow);
        if (isRoyale(data.gameType)) {
          routeRoyaleSpectateStart(payload, myUserId);
        }
      },

      // Puzzle Royale spectate: a player's per-ply board stream. Royale only.
      fun_spectate_move(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<any>(data.payload);
        if (!payload) return;
        if (isRoyale(data.gameType)) {
          routeRoyaleSpectateMove(payload);
        }
      },

      fun_session_rejoin(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunSessionRejoinPayload>(data.payload);
        if (!payload) return;
        // MOST important calibration point: a cold mid-race rejoin never sees
        // the 3-2-1, so there is no local anchor to fall back on and this path
        // was fully exposed to device-clock skew.
        noteServerNow((payload as any).serverNow);

        if (isRoyale(data.gameType)) {
          routeRoyaleSessionRejoin(payload as any, myUserId);
          return;
        }

        const opponentId =
          payload.playerIds.find(id => String(id) !== String(myUserId)) || '';

        // Older server builds may omit startsAt — default to 0 so the
        // client treats the rejoined session as already-started (no
        // countdown overlay) instead of stalling forever.
        const startsAt = parseInt(payload.startsAt || '', 10) || 0;
        setSession({
          sessionId: payload.sessionId,
          roomId: payload.roomId,
          gameType: data.gameType,
          playerIds: payload.playerIds,
          endsAt: parseInt(payload.endsAt, 10) || 0,
          settings: payload.settings,
          coords: payload.coords || [],
        });

        const myScores = payload.scores?.[String(myUserId || '')] || {
          correct: 0,
          wrong: 0,
        };
        const oppScores = payload.scores?.[String(opponentId)] || {
          correct: 0,
          wrong: 0,
        };

        const rejoinOpponentName =
          payload.players?.find(
            p => String(p.userId) === String(opponentId),
          )?.username || '';

        // Reconstruct each player's traffic-light history row from the
        // server's per-player response trail.
        //
        // Two reconstruction strategies depending on the challenge type:
        //   - Square Trainer: response[i] is the square the player clicked;
        //     correctness is `response[i] === coords[i]` (the shared
        //     server-pushed sequence).
        //   - Position Trainer Battle + Puzzle Dash Battle: response[i] is
        //     the overloaded "<puzzleId>:<uci>:<0|1>" string the adapter
        //     emitted (one per terminal puzzle event). The trailing bit IS
        //     the correctness — we don't need to compare against `coords`.
        //     PDB uses the SAME adapter as PTB so the same suffix-parsing
        //     path is correct for both.
        const coordsArr = payload.coords || [];
        const incomingGameType =
          (data.gameType || '') ||
          (payload.settings?.battleType as string) ||
          '';
        const isPTBLikeWalker =
          incomingGameType === BattleType.POSITION_TRAINER ||
          incomingGameType === BattleType.PUZZLE_DASH;

        const reconstructMoves = (responses?: string[]): boolean[] => {
          if (!responses || responses.length === 0) return [];
          if (isPTBLikeWalker) {
            // Parse the trailing `:0|1` correctness bit. Anything malformed
            // is treated as a wrong move so the row stays the right length
            // and the adapter's mid-line state can be approximated from it.
            return responses.map(raw => {
              if (typeof raw !== 'string' || !raw) return false;
              const lastColon = raw.lastIndexOf(':');
              if (lastColon === -1) return false;
              const bit = raw.slice(lastColon + 1);
              return bit === '1' || bit === 'true';
            });
          }
          // Square Trainer: align response squares against coords by index.
          return responses.map((sq, i) => {
            const target = coordsArr[i % Math.max(1, coordsArr.length)] ?? '';
            return sq === target;
          });
        };
        const myMovesReconstructed = reconstructMoves(payload.myResponses);

        // Opponent reconstruction is preferred via `allResponses` (per-move
        // truth), but we fall back to an approximation built from cumulative
        // scores when the server omits the field — that keeps the row
        // non-empty for back-compat with older cc-game-service builds. The
        // approximation is `[true × correct, false × wrong]` — count is
        // correct, order is "all greens then all reds" instead of the real
        // interleave. Once any new `fun_move_broadcast` arrives the
        // idempotency guard keeps the row in sync from then on.
        const opponentResponses =
          payload.allResponses?.[String(opponentId)];
        let opponentMovesReconstructed: boolean[];
        if (opponentResponses && opponentResponses.length > 0) {
          opponentMovesReconstructed = reconstructMoves(opponentResponses);
        } else {
          if (!payload.allResponses) {
            console.warn(
              '[FunChallenges] rejoin payload missing allResponses — ' +
                'cc-game-service may need a restart to pick up the patch; ' +
                'opponent row will use an approximation from cumulative scores.',
            );
          }
          const c = oppScores.correct;
          const w = oppScores.wrong;
          opponentMovesReconstructed = [
            ...Array(c).fill(true),
            ...Array(w).fill(false),
          ];
        }

        battleHydrate(
          {
            battleId: payload.sessionId,
            battleType: asBattleType(data.gameType),
            myUserId: String(myUserId || ''),
            opponentId: String(opponentId),
            opponentName: rejoinOpponentName,
            startsAt,
            endsAt: parseInt(payload.endsAt, 10) || 0,
            settings: payload.settings,
            coords: coordsArr,
          },
          {
            myCorrect: myScores.correct,
            myWrong: myScores.wrong,
            opponentCorrect: oppScores.correct,
            opponentWrong: oppScores.wrong,
          },
          payload.myResponses || [],
          {
            myMoves: myMovesReconstructed,
            opponentMoves: opponentMovesReconstructed,
          },
        );
      },

      fun_player_eliminated(_socket: any, data: FunChallengeEnvelope) {
        // Royale-only: a player hit the punish mistake-out limit. Mark them
        // out (they stay ranked); the race continues for everyone else.
        const payload = parsePayload<{playerId: string}>(data.payload);
        if (!payload) return;
        if (isRoyale(data.gameType)) {
          routeRoyalePlayerEliminated(payload);
        }
      },

      fun_rematch(socket: any, data: FunChallengeEnvelope) {
        // Royale-only: the host started a rematch. Seed the new lobby for
        // everyone (the global listener then navigates to PuzzleRoyaleBattle's
        // waiting room). The host is already in the new room server-side; every
        // NON-host must actually join the new code so the server adds them and
        // the roster syncs.
        if (!isRoyale(data.gameType)) return;
        const payload = parsePayload<{
          roomId: string;
          shareCode: string;
          hostId: string;
          settings?: any;
        }>(data.payload);
        if (!payload) return;
        const {isHost, shareCode} = routeRoyaleRematch(payload, myUserId);
        if (!isHost && socket && typeof socket.send_msg === 'function') {
          try {
            socket.send_msg('fun_room_join', {
              requestId: String(Date.now()),
              type: 'fun_room_join',
              gameType: data.gameType,
              id: String(shareCode || ''),
              // Send our real name/avatar so the rematch roster isn't blank (#1).
              payload: JSON.stringify({
                username: myUsername || '',
                avatarUrl: myAvatarUrl || '',
              }),
            });
          } catch (err) {
            console.warn('[FunChallenges] rematch auto-join failed', err);
          }
        }
      },

      fun_error(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunChallengeError>(data.payload);
        if (!payload) return;
        // Silence the speculative-rejoin probe responses: when there's no
        // active session for this user the server replies with
        // NO_ACTIVE_SESSION / SESSION_ENDED / SESSION_NOT_FOUND. None of
        // these are real failures from the user's perspective — they just
        // mean "nothing to rejoin", which is the expected state for a
        // fresh login or post-game socket.
        if (
          data.requestId === REJOIN_PROBE_REQUEST_ID &&
          (payload.code === 'NO_ACTIVE_SESSION' ||
            payload.code === 'SESSION_ENDED' ||
            payload.code === 'SESSION_NOT_FOUND')
        ) {
          return;
        }
        // Royale errors go to the royale store (not the 1v1 store). The most
        // common one a user hits is joining a code AFTER the race started
        // (ROOM_NOT_WAITING) — give it friendly copy. (Full auto-spectate on
        // late join is PR-7, once the server spectate path exists; clear the
        // optimistic lobby seed so the joiner isn't stuck on an empty waiting
        // room.)
        if (isRoyale(data.gameType)) {
          // COUNTDOWN_IN_PROGRESS is BENIGN and self-correcting: the player's
          // move raced slightly ahead of the server's countdown end (typically a
          // client clock skewed fast — mostly prevented by the duration-anchored
          // countdown in PuzzleRoyaleBattle, but residual network jitter / an
          // in-flight move can still trip it). The move is simply dropped
          // server-side; NO local state is advanced or locked (onMove is
          // fire-and-forget; the puzzle only advances on the authoritative
          // fun_move_broadcast). So the board stays live and the player's NEXT
          // click — now past starts_at — lands normally. Swallow it silently so
          // we never surface a "Battle has not started yet" banner for a
          // harmless timing edge. Log for telemetry only.
          if (payload.code === 'COUNTDOWN_IN_PROGRESS') {
            console.log(
              '[royale] swallowed benign COUNTDOWN_IN_PROGRESS (move before start); player may retry',
            );
            return;
          }
          // SESSION_NOT_ACTIVE ("Session already ended") is the MIRROR IMAGE of
          // the above: a move that lands just AFTER the clock ran out. The
          // server drops it and scores nothing, which is correct — but banner-ing
          // "Session already ended" is pure noise, because the results modal is
          // already on its way from fun_session_end and tells the player the race
          // is over. Nothing local is advanced or locked (onMove is
          // fire-and-forget; the puzzle only advances on the authoritative
          // fun_move_broadcast), so silently dropping it is exactly the intended
          // behaviour. Log for telemetry only.
          if (payload.code === 'SESSION_NOT_ACTIVE') {
            console.log(
              '[royale] swallowed benign SESSION_NOT_ACTIVE (move after end); results modal covers it',
            );
            return;
          }
          if (payload.code === 'ROOM_NOT_WAITING') {
            useFunChallengesRoyaleStore.getState().reset();
            useFunChallengesRoyaleStore
              .getState()
              .setError(
                'This race has already started — ask the host for a spectate link or try a new lobby.',
                payload.code,
              );
          } else if (payload.code === 'REGISTER_FAILED') {
            // Friendly copy for the common register rejections (Django reason
            // arrives as payload.message: time_overlap / full / registration_closed).
            const reason = String(payload.message || '');
            const friendly =
              reason === 'time_overlap'
                ? "You're already registered for another Puzzle Royale tournament at that time."
                : reason === 'full'
                ? 'This tournament is full.'
                : reason === 'registration_closed'
                ? 'Registration has closed — the race has already started.'
                : "Couldn't register. Please try again.";
            useFunChallengesRoyaleStore.getState().setError(friendly, payload.code);
          } else {
            useFunChallengesRoyaleStore
              .getState()
              .setError(payload.message || 'Something went wrong.', payload.code);
          }
          return;
        }
        battleSetError(payload);
      },

      // ── Puzzle Royale Tournament ─────────────────────────────────────────
      fun_tournament_created(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<{tournament?: {id: number}}>(data.payload);
        const id = payload?.tournament?.id;
        if (id != null) {
          useFunChallengesTournamentStore.getState().setCreated(String(id));
        }
      },
      fun_tournament_state(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<{
          tournamentId?: string;
          state?: string;
          reason?: string;
          shareCode?: string;
          roomId?: string;
          player_count?: number;
        }>(data.payload);
        if (!payload || !payload.tournamentId) return;
        useFunChallengesTournamentStore.getState().setState({
          tournamentId: String(payload.tournamentId),
          state: payload.state || '',
          reason: payload.reason,
          shareCode: payload.shareCode,
          roomId: payload.roomId,
          playerCount: payload.player_count,
          at: Date.now(),
        });
      },
    };
  }, [
    battleSetIncoming,
    battleStartInBattle,
    battleEnd,
    battleSetError,
    battleHydrate,
    battleRecordOpponentMove,
    setRoom,
    setRoomPlayers,
    setRoomState,
    setSession,
    clearSession,
    myUserId,
    myUsername,
    myAvatarUrl,
  ]);

  // ── Schema registration — once per socket identity ────────────────────
  // The SDK's `registerMessageType` is idempotent on schema (it just
  // overwrites `schemaTypeDict[msgType]`), so re-running is safe — but we
  // also use the registry store to fire auto-rejoin exactly once per new
  // socket.
  useEffect(() => {
    if (!socket) return;

    if (!schema?.funChallengeEnvelopeSchema) {
      console.warn(
        '[FunChallenges] cc-client-sdk does not export funChallengeEnvelopeSchema — ' +
          'check the local install (npm install ../cc-client-sdk).',
      );
      return;
    }

    const envelope = schema.funChallengeEnvelopeSchema;
    const isNewSocket = registeredSocket !== socket;

    if (isNewSocket) {
      FUN_INBOUND_TYPES.forEach(msgType => {
        try {
          socket.registerMessageType(msgType, envelope, undefined);
          hardenDecode(socket, msgType);
        } catch (err) {
          console.warn(
            '[FunChallenges] inbound schema register failed for',
            msgType,
            err,
          );
        }
      });

      FUN_OUTBOUND_TYPES.forEach(msgType => {
        try {
          socket.registerMessageType(msgType, envelope, undefined);
          hardenDecode(socket, msgType);
        } catch (err) {
          console.warn(
            '[FunChallenges] outbound schema register failed for',
            msgType,
            err,
          );
        }
      });

      setRegisteredSocket(socket);
    }

    // Sweep EVERY decoder registered on this socket — not just our fun_* types
    // but also ones registered by sibling hooks (e.g. online_players_result
    // from useFunChallengesUserSearch). socket.io delivers binary frames to the
    // browser as {type:'Buffer',data:[…]}, which the stock decoder chokes on;
    // hardening here makes every inbound type crash-safe. Idempotent.
    hardenAllDecoders(socket);
  }, [socket, registeredSocket, setRegisteredSocket]);

  // ── Probe for an active session — gated on auth, in its OWN effect ──────
  // The local Zustand store does NOT persist across hard refreshes, so we
  // can't gate this on `battle.status === 'in_battle'` — that path skipped
  // the probe on refresh and dumped the user back on the lobby. The server's
  // `getPlayerActiveSession` (keyed on authenticated userId) is canonical; it
  // replies with `fun_session_rejoin` (active session) or
  // `fun_error: NO_ACTIVE_SESSION / SESSION_ENDED / SESSION_NOT_FOUND`.
  //
  // CRITICAL: this lives in a SEPARATE effect keyed only on `[socket]`. An
  // earlier version registered the `auth_complete` listener inside the
  // schema-registration effect, whose `setRegisteredSocket(socket)` mutated a
  // dependency and re-ran the effect — React then fired the cleanup and tore
  // the listener back off before auth ever completed, so the probe never
  // sent. Keying on `[socket]` alone makes the listener stable for the life
  // of the socket.
  //
  // We also fire immediately if the socket is ALREADY authenticated (the SDK
  // sets `socket.authenticated = true` on auth_complete), since auth often
  // completes before this hook's effects run and we'd otherwise miss the
  // one-shot event. Idempotent server-side (read-only) and tagged with
  // REJOIN_PROBE_REQUEST_ID so the error handler suppresses NO_*/SESSION_*.
  useEffect(() => {
    if (!socket || typeof socket.send_msg !== 'function') return;

    const sendProbe = () => {
      try {
        // Once per socket identity across ALL consumers — see PROBED_SOCKETS.
        // Many components mount this hook; without this, each fires a probe and
        // a re-render storm floods fun_session_rejoin, starving fun_room_create.
        if (PROBED_SOCKETS.has(socket)) {
          return;
        }
        // CRITICAL: ensure the schema is registered before send_msg. If the
        // schema-registration effect hasn't run yet on this socket, send_msg
        // would emit a RAW OBJECT for fun_session_rejoin, which the server's
        // binary decoder rejects and then echoes back a malformed fun_error
        // that the client also fails to decode (the schemapack "Received type
        // object" crash). Late-register here, and refuse to send if we still
        // have no schema, exactly like the guarded `send()` path below.
        if (
          !socket.schemaTypeDict?.['fun_session_rejoin'] &&
          typeof socket.registerMessageType === 'function' &&
          schema?.funChallengeEnvelopeSchema
        ) {
          socket.registerMessageType(
            'fun_session_rejoin',
            schema.funChallengeEnvelopeSchema,
            undefined,
          );
          hardenDecode(socket, 'fun_session_rejoin');
        }
        if (!socket.schemaTypeDict?.['fun_session_rejoin']) {
          console.error(
            '[FunChallenges] refusing rejoin probe — no schema for fun_session_rejoin',
          );
          return;
        }
        const battle = useFunChallengesBattleStore.getState();
        socket.send_msg('fun_session_rejoin', {
          requestId: REJOIN_PROBE_REQUEST_ID,
          type: 'fun_session_rejoin',
          gameType:
            (battle.session?.battleType as string) || 'fun_square_trainer',
          id: '',
          payload: '{}',
        });
        // Mark probed only AFTER a successful send (a refused/no-schema early
        // return above leaves it unprobed so a later mount can retry once).
        PROBED_SOCKETS.add(socket);
      } catch (err) {
        console.warn('[FunChallenges] auth-gated rejoin send failed', err);
      }
    };

    if (socket.authenticated) {
      // auth_complete already fired before we attached — probe now.
      sendProbe();
    }
    if (typeof socket.on === 'function') {
      socket.on('auth_complete', sendProbe);
    }
    return () => {
      try {
        socket.off?.('auth_complete', sendProbe);
      } catch {
        // ignore cleanup failures
      }
    };
  }, [socket]);

  // 2026-05-21 bug 3 (belt-and-suspenders): re-fire the rejoin probe
  // whenever `myUserId` transitions from empty → non-empty. The PRIMARY gate
  // is now the `auth_complete` listener in the identity-change effect above;
  // this covers the case where myUserId resolves after auth, or where
  // auth_complete already fired before the listener attached. After a hard
  // refresh:
  //   1. Socket reconnects; the identity-change effect attaches the
  //      auth_complete listener that fires the FIRST probe once the server
  //      validates the token.
  //   2. Login API returns; userData populates; `myUserId` flips to a
  //      real value. We fire a SECOND probe so the server gets a chance
  //      to return our active session.
  //
  // The probe is idempotent server-side (read-only `getPlayerActiveSession`),
  // and the client's `fun_session_rejoin` handler is idempotent too
  // (setSession + hydrate just overwrite). Worst case the user pays one
  // extra socket roundtrip.
  const prevMyUserIdRef = useRef<string>('');
  useEffect(() => {
    if (!socket || typeof socket.send_msg !== 'function') return;
    const prev = prevMyUserIdRef.current;
    prevMyUserIdRef.current = myUserId;
    if (prev) return;            // we already had an id; not the transition we care about
    if (!myUserId) return;       // still empty
    try {
      // Guard: ensure the schema is registered before send_msg so we never
      // emit a raw object (see the auth-gated probe above for why that
      // triggers the schemapack "Received type object" crash).
      if (
        !socket.schemaTypeDict?.['fun_session_rejoin'] &&
        typeof socket.registerMessageType === 'function' &&
        schema?.funChallengeEnvelopeSchema
      ) {
        socket.registerMessageType(
          'fun_session_rejoin',
          schema.funChallengeEnvelopeSchema,
          undefined,
        );
        hardenDecode(socket, 'fun_session_rejoin');
      }
      if (!socket.schemaTypeDict?.['fun_session_rejoin']) {
        console.error(
          '[FunChallenges] refusing rejoin probe (myUserId) — no schema',
        );
        return;
      }
      socket.send_msg('fun_session_rejoin', {
        requestId: REJOIN_PROBE_REQUEST_ID,
        type: 'fun_session_rejoin',
        gameType: 'fun_square_trainer',
        id: '',
        payload: '{}',
      });
    } catch (err) {
      console.warn('[FunChallenges] auto-rejoin (myUserId resolved) failed', err);
    }
  }, [socket, myUserId]);

  // ── Handler (re-)installation — runs whenever the handlers closure
  // changes (e.g. after `myUserId` resolves) so the latest closure
  // replaces the previously-keyed one. The SDK's keyed registry replaces
  // by `handlerKey`, so this is idempotent and won't duplicate listeners.
  useEffect(() => {
    if (!socket) return;
    if (typeof socket.registerMessageHandler !== 'function') return;
    FUN_INBOUND_TYPES.forEach(msgType => {
      try {
        socket.registerMessageHandler(
          msgType,
          handlers[msgType],
          HANDLER_PREFIX + msgType,
        );
      } catch (err) {
        console.warn('[FunChallenges] handler attach failed for', msgType, err);
      }
    });
  }, [socket, handlers]);

  // ── Outbound helper ───────────────────────────────────────────────────
  const api = useMemo<FunChallengesSocketApi>(() => {
    return {
      socket,
      ready,
      authed,
      send: ({type, gameType, id = '', payload = {}, requestId = ''}: SendArgs) => {
        if (!socket || typeof socket.send_msg !== 'function') return false;
        // CRITICAL: cc-client-sdk's send_msg falls back to emitting the
        // raw JS object when the message type isn't in schemaTypeDict.
        // The server's binary decoder then rejects it with
        // "ERR_INVALID_ARG_TYPE: first argument must be string or
        // Buffer", AND the routing.js error handler then crashes trying
        // to set `data.userName` on undefined — uncaughtException, room
        // half-created, client stuck. Happens when send() fires before
        // the schema-registration useEffect has run (race on socket
        // identity change / fresh mount). Register on the fly here so
        // we never emit a raw object for fun_* types.
        try {
          const dict = socket.schemaTypeDict || {};
          if (
            !dict[type] &&
            typeof socket.registerMessageType === 'function' &&
            schema?.funChallengeEnvelopeSchema
          ) {
            // Late-register from the SDK export if the registration
            // useEffect hasn't run yet on this socket (race on fresh
            // mount). If the SDK doesn't export the schema at all,
            // there's nothing we can do here — fall through to the
            // refuse-to-send guard below.
            socket.registerMessageType(
              type,
              schema.funChallengeEnvelopeSchema,
              undefined,
            );
            hardenDecode(socket, type);
            console.warn(
              '[FunChallenges] late-registered missing schema for',
              type,
            );
          }
          if (!socket.schemaTypeDict?.[type]) {
            // Refuse rather than emit a raw object that would brick
            // the server (cc-conn-mgr/routing.js:1264 crashes when it
            // tries to read `data.userName` on a failed binary decode).
            console.error(
              '[FunChallenges] refusing to send — no schema for',
              type,
            );
            return false;
          }
        } catch (regErr) {
          console.warn('[FunChallenges] schema check/register failed', regErr);
        }
        try {
          socket.send_msg(type, {
            requestId: requestId || String(Date.now()),
            type,
            gameType: String(gameType || ''),
            id,
            payload: JSON.stringify(payload),
          });
          return true;
        } catch (err) {
          console.warn('[FunChallenges] send failed for', type, err);
          return false;
        }
      },
    };
  }, [socket, ready, authed]);

  return api;
}
